import React, { useState, useEffect } from 'react';
import { 
  X, BarChart3, Users, DollarSign, Calendar, TrendingUp, 
  Loader2, RefreshCw, CreditCard, MessageSquare, UsersRound,
  Sparkles, Image as ImageIcon
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'reggie@coolgeek.me';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];
const PLATFORM_COLORS = {
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  facebook: '#1877F2',
  tiktok: '#000000',
  bluesky: '#0085FF'
};

const AnalyticsDashboard = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [summaryRes, revenueRes, usageRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/analytics/summary?admin_email=${ADMIN_EMAIL}`),
        fetch(`${BACKEND_URL}/api/admin/analytics/revenue?admin_email=${ADMIN_EMAIL}`),
        fetch(`${BACKEND_URL}/api/admin/analytics/usage?admin_email=${ADMIN_EMAIL}`)
      ]);
      
      if (!summaryRes.ok || !revenueRes.ok || !usageRes.ok) {
        throw new Error('Failed to fetch analytics');
      }
      
      const [summaryData, revenueData, usageData] = await Promise.all([
        summaryRes.json(),
        revenueRes.json(),
        usageRes.json()
      ]);
      
      setSummary(summaryData);
      setRevenue(revenueData);
      setUsage(usageData);
    } catch (err) {
      console.error('Analytics error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo' }) => (
    <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 bg-${color}-600/20 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        {subValue && (
          <span className="text-xs text-slate-500">{subValue}</span>
        )}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-slate-400 mt-1">{label}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Analytics Dashboard</h2>
              <p className="text-sm text-slate-400">Overview of platform performance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAnalytics}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchAnalytics}
                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  icon={Users} 
                  label="Total Users" 
                  value={summary?.users?.total || 0}
                  subValue={`${summary?.users?.active_subscribers || 0} active`}
                />
                <StatCard 
                  icon={DollarSign} 
                  label="Total Revenue" 
                  value={`$${(summary?.transactions?.revenue || 0).toLocaleString()}`}
                  color="green"
                />
                <StatCard 
                  icon={Calendar} 
                  label="Calendars Generated" 
                  value={summary?.content?.calendars_generated || 0}
                  color="violet"
                />
                <StatCard 
                  icon={UsersRound} 
                  label="Teams Created" 
                  value={summary?.engagement?.teams_created || 0}
                  color="amber"
                />
              </div>

              {/* Second Row Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  icon={CreditCard} 
                  label="Transactions" 
                  value={summary?.transactions?.total || 0}
                />
                <StatCard 
                  icon={Sparkles} 
                  label="Profiles Created" 
                  value={summary?.content?.profiles_created || 0}
                  color="pink"
                />
                <StatCard 
                  icon={MessageSquare} 
                  label="Contact Messages" 
                  value={summary?.engagement?.contact_submissions || 0}
                  color="cyan"
                />
                <StatCard 
                  icon={TrendingUp} 
                  label="Lifetime Users" 
                  value={summary?.users?.lifetime_users || 0}
                  color="emerald"
                />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Platform Popularity */}
                <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-400" />
                    Platform Popularity
                  </h3>
                  {usage?.platforms && usage.platforms.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={usage.platforms}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="platform" 
                          tick={{ fill: '#9ca3af', fontSize: 12 }}
                          tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                        />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #374151',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar 
                          dataKey="count" 
                          fill="#6366f1" 
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-slate-500">
                      No platform data yet
                    </div>
                  )}
                </div>

                {/* Subscription Distribution */}
                <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-violet-400" />
                    Subscription Distribution
                  </h3>
                  {usage?.subscriptions && usage.subscriptions.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={usage.subscriptions}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="type"
                          label={({ type, count }) => `${type}: ${count}`}
                        >
                          {usage.subscriptions.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #374151',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-slate-500">
                      No subscription data yet
                    </div>
                  )}
                </div>
              </div>

              {/* Revenue by Package Type */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Revenue by Package Type
                </h3>
                {revenue?.by_type && revenue.by_type.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {revenue.by_type.map((item, index) => (
                      <div key={item.type} className="bg-slate-800 rounded-lg p-4">
                        <p className="text-sm text-slate-400 capitalize">{item.type}</p>
                        <p className="text-xl font-bold text-white">${item.revenue.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">{item.count} transactions</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No revenue data yet
                  </div>
                )}
              </div>

              {/* Average Credits */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Average User Credits
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-800 rounded-lg">
                    <Calendar className="w-6 h-6 text-indigo-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{usage?.avg_credits?.calendars || 0}</p>
                    <p className="text-sm text-slate-400">Avg Calendars</p>
                  </div>
                  <div className="text-center p-4 bg-slate-800 rounded-lg">
                    <Sparkles className="w-6 h-6 text-violet-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{usage?.avg_credits?.content_regens || 0}</p>
                    <p className="text-sm text-slate-400">Avg Content Regens</p>
                  </div>
                  <div className="text-center p-4 bg-slate-800 rounded-lg">
                    <ImageIcon className="w-6 h-6 text-pink-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{usage?.avg_credits?.image_regens || 0}</p>
                    <p className="text-sm text-slate-400">Avg Image Regens</p>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Recent Transactions
                </h3>
                {revenue?.recent && revenue.recent.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-slate-400 border-b border-slate-700">
                          <th className="pb-3 pr-4">Email</th>
                          <th className="pb-3 pr-4">Package</th>
                          <th className="pb-3 pr-4">Amount</th>
                          <th className="pb-3">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {revenue.recent.slice(0, 10).map((tx, index) => (
                          <tr key={tx.id || index} className="border-b border-slate-800">
                            <td className="py-3 pr-4 text-sm text-white">{tx.user_email}</td>
                            <td className="py-3 pr-4 text-sm text-slate-300 capitalize">
                              {tx.package_type}: {tx.package_id}
                            </td>
                            <td className="py-3 pr-4 text-sm text-green-400">${tx.amount}</td>
                            <td className="py-3 text-sm text-slate-500">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No transactions yet
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
